import { PillChart } from '@repo/videa-components/src/common-charts/index';
import { useAtomValue } from 'jotai';
import { chartColors } from 'node_modules/@repo/videa-components/src/common-charts/themes/colors';

import { isCountsAtom } from '../atoms';
import { TrendIndicator } from '../components/TrendIndicator';
import { useGetFilteredHygieneData } from '../helpers/data/getHygieneData';
import { useCalculateHygieneBenchmark } from '../helpers/filterHygieneData';
import { formatLargeNumber } from '../helpers/formatNumber';
import type { TTotalOpp } from '../types';

import { BenchmarkViz } from './BenchmarkViz';

export const TotalTaskOppChart = ({ totalOpp }: { totalOpp?: TTotalOpp }) => {
  const isCounts = useAtomValue(isCountsAtom);

  const { filteredData: hygieneData } = useGetFilteredHygieneData();
  const { lowerBenchmark, upperBenchmark, providerRatesDistribution } =
    useCalculateHygieneBenchmark(hygieneData);

  const opportunityRate = totalOpp?.current
    ? totalOpp.current.completed /
      (totalOpp.current.opportunity + totalOpp.current.completed)
    : undefined;

  return (
    <div className="vai-bg-white vai-rounded-lg vai-flex vai-flex-col vai-p-4 vai-border-gray-200 vai-border-[1px] vai-w-[40%] vai-gap-6">
      <div className="vai-flex vai-flex-col">
        <span className="vai-typography-h4">Task Completion</span>
        <span className="vai-typography-muted">
          What % of Hygiene tasks due were completed and how much is still
          outstanding?
        </span>
      </div>

      {totalOpp?.current ? (
        <>
          <div className="vai-flex vai-flex-row vai-justify-center vai-gap-2">
            <div className="vai-flex vai-flex-col vai-border-r-2 vai-border-gray-200 vai-w-60">
              <span className="vai-text-lg vai-flex vai-flex-row vai-gap-2 vai-items-center">
                <div className="vai-w-4 vai-bg-chart-normal-2 vai-rounded-sm vai-h-4" />
                Completed Tasks
              </span>
              <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-text-4xl vai-font-[500] vai-pl-4">
                {formatLargeNumber(
                  totalOpp.current.completed,
                  isCounts ? '' : '$',
                )}
                {totalOpp.previous && (
                  <TrendIndicator
                    value={Math.round(
                      ((totalOpp.current.completed -
                        totalOpp.previous.completed) /
                        totalOpp.previous.completed) *
                        100,
                    )}
                    suffix="%"
                  />
                )}
              </div>
            </div>
            <div className="vai-flex vai-flex-col vai-ml-4 vai-w-60">
              <span className="vai-text-lg vai-flex vai-flex-row vai-gap-2 vai-items-center">
                <div className="vai-w-4 vai-bg-chart-tint-2 vai-rounded-sm vai-h-4" />
                Uncompleted Tasks
              </span>
              <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-text-4xl vai-font-[500] vai-pl-4">
                {formatLargeNumber(
                  totalOpp.current.opportunity,
                  isCounts ? '' : '$',
                )}
                {totalOpp.previous && (
                  <TrendIndicator
                    value={Math.round(
                      ((totalOpp.current.opportunity -
                        totalOpp.previous.opportunity) /
                        totalOpp.previous.opportunity) *
                        100,
                    )}
                    suffix="%"
                    decreasingIsPositive={true}
                  />
                )}
              </div>
            </div>
            <div className="vai-flex vai-flex-col"></div>
          </div>

          <div className="vai-flex vai-flex-col vai-w-full">
            <div className="vai-flex vai-flex-row vai-justify-end vai-z-10 vai-gap-2">
              <span className="vai-typography-muted">Total Tasks Due: </span>
              <span className="vai-typography-small">
                {formatLargeNumber(
                  totalOpp.current.opportunity + totalOpp.current.completed,
                  isCounts ? '' : '$',
                )}
              </span>
            </div>
            <div className="vai-relative vai-my-[-10px] vai-z-0">
              <PillChart
                data={[
                  {
                    name: 'Uncompleted Tasks',
                    data: totalOpp.current.opportunity,
                  },
                  {
                    name: 'Completed Tasks',
                    data: totalOpp.current.completed,
                  },
                ]}
                chartOptions={{
                  colors: [chartColors['chart-tint'][2], chartColors.chart[2]],
                  height: 150,
                  yAxisMin: -1 * totalOpp.current.completed,
                  yAxisMax: totalOpp.current.opportunity,
                  tooltipFormatter: function (this: any) {
                    return `${
                      this.series.userOptions.name
                    }: ${formatLargeNumber(Math.abs(this.y), isCounts ? '' : '$')}`;
                  },
                  yAxisFormatter: function (this: any) {
                    if (this.value === 0) {
                      return `<span style="font-size: 24px;">${Math.round(
                        (opportunityRate ?? 0) * 100,
                      )}%</span>`;
                    }
                    return '';
                  },
                }}
              />
            </div>

            {opportunityRate !== undefined && (
              <div className="vai-flex vai-flex-row vai-p-4 vai-h-full vai-bg-zinc-50 vai-rounded-sm vai-w-full vai-justify-between vai-items-center vai-gap-4">
                <div className="vai-flex vai-flex-col vai-gap-4">
                  <span className="vai-typography-muted">
                    TASK COMPLETION RATE
                  </span>

                  <div className="vai-flex vai-flex-row vai-gap-4 vai-items-center">
                    <span className="vai-text-4xl vai-font-semi-bold">
                      {Math.round(opportunityRate * 100)}%
                    </span>
                    <span>
                      Completed of the{' '}
                      {formatLargeNumber(
                        totalOpp.current.opportunity +
                          totalOpp.current.completed,
                        isCounts ? '' : '$',
                      )}{' '}
                      available tasks due
                    </span>
                  </div>
                </div>

                <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-w-1/2">
                  <BenchmarkViz
                    opportunityRate={opportunityRate}
                    lowerBenchmark={lowerBenchmark}
                    upperBenchmark={upperBenchmark}
                    providerRatesDistribution={providerRatesDistribution}
                    isClinical={false}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="vai-flex vai-p-4">
          <img
            src="/assets/images/insights/totalOppLoading.png"
            alt="loading"
            className="vai-w-full vai-opacity-20"
          />
        </div>
      )}
    </div>
  );
};
